import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  PitchSlide,
  SlideBackground,
  SlideComponent,
  SlideComponentStyle,
  SlideElementId,
  SlideImageStyle,
  SlideLayout,
  SlideChartStyle,
  SlideChartType,
  SlideTableStyle,
} from '../../data/slides';
import { LayerId } from '../../data/layerOrder';
import {
  addChartPoint,
  addTableColumn,
  addTableRow,
  getComponentById,
  getComponentLabel,
  isComponentId,
  removeChartPoint,
  removeTableColumn,
  removeTableRow,
  updateChartLabel,
  updateChartTitle,
  updateChartType,
  updateChartValue,
  updateTableCell,
  updateTableHeader,
} from '../../data/slideComponents';
import { appTheme } from '../../styles/theme';
import { formatImageUploadError } from '../../utils/formatImageUploadError';
import { MAX_SLIDE_IMAGE_BYTES, readImageUpload } from '../../utils/imageUpload';
import { clampImageCornerRadius, maxImageCornerRadius } from '../../utils/imageCornerRadius';
import DeckLogoPanel from './DeckLogoPanel';

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Heading = styled.h3`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${appTheme.text};
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: ${appTheme.textMuted};
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${appTheme.textMuted};
`;

const TextInput = styled.input`
  padding: 8px 10px;
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusSm};
  font-family: inherit;
  font-size: 0.875rem;
  color: ${appTheme.text};
  background: ${appTheme.surface};
  text-transform: none;
  letter-spacing: normal;
  font-weight: 400;

  &:focus {
    outline: 2px solid ${appTheme.green};
    outline-offset: -1px;
  }
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusSm};
  font-family: inherit;
  font-size: 0.875rem;
  color: ${appTheme.text};
  background: ${appTheme.surface};
  text-transform: none;
  letter-spacing: normal;
  font-weight: 400;
`;

const RangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RangeInput = styled.input`
  flex: 1;
  accent-color: ${appTheme.green};
`;

const RangeValue = styled.span`
  min-width: 42px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${appTheme.text};
  text-transform: none;
  letter-spacing: normal;
`;

const HighlightRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const RemoveButton = styled.button`
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusSm};
  background: ${appTheme.surface};
  color: ${appTheme.textMuted};
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: ${appTheme.dangerBg};
    color: ${appTheme.dangerText};
    border-color: ${appTheme.dangerBorder};
  }
`;

const AddButton = styled.button`
  align-self: flex-start;
  padding: 8px 14px;
  border: 1px dashed ${appTheme.green};
  border-radius: ${appTheme.radiusSm};
  background: transparent;
  color: ${appTheme.green};
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${appTheme.mint};
  }
`;

const ActionButton = styled.button`
  align-self: flex-start;
  padding: 8px 14px;
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusSm};
  background: ${appTheme.surface};
  color: ${appTheme.text};
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${appTheme.surfaceHover};
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const DeleteButton = styled.button`
  align-self: flex-start;
  padding: 8px 14px;
  border: 1px solid ${appTheme.dangerBorder};
  border-radius: ${appTheme.radiusSm};
  background: ${appTheme.dangerBg};
  color: ${appTheme.dangerText};
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    filter: brightness(0.97);
  }
`;

const ColorRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ColorSwatch = styled.input`
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  padding: 2px;
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusSm};
  cursor: pointer;
  background: ${appTheme.surface};
`;

const TableEditSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TableEditRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const CompactInput = styled(TextInput)`
  padding: 6px 8px;
  font-size: 0.8125rem;
`;

const TableActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Subheading = styled.h4`
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${appTheme.textMuted};
`;

export const ELEMENT_LABEL_KEYS: Record<SlideElementId, string> = {
  background: 'editor.layers.background',
  logo: 'editor.layers.logo',
  title: 'editor.layers.title',
  highlights: 'editor.layers.highlights',
  image: 'editor.layers.image',
};

interface ElementInspectorProps {
  selectedLayerId: LayerId | null;
  slide: PitchSlide;
  layout: SlideLayout;
  logoUrl: string | null;
  onLogoChange: (logoUrl: string | null) => void;
  onSlideChange: (changes: Partial<PitchSlide>) => void;
  onBackgroundChange: (background: SlideBackground) => void;
  onImageStyleChange: (imageStyle: SlideImageStyle) => void;
  onComponentChange: (componentId: string, changes: Partial<SlideComponent>) => void;
  onComponentRemove: (componentId: string) => void;
}

const ElementInspector: React.FC<ElementInspectorProps> = ({
  selectedLayerId,
  slide,
  layout,
  logoUrl,
  onLogoChange,
  onSlideChange,
  onBackgroundChange,
  onImageStyleChange,
  onComponentChange,
  onComponentRemove,
}) => {
  const { t } = useTranslation();
  const imageInputRef = useRef<HTMLInputElement>(null);
  const componentImageInputRef = useRef<HTMLInputElement>(null);
  const builtInImageMaxRadius = maxImageCornerRadius(layout.image);
  const builtInImageRadius = clampImageCornerRadius(
    layout.imageStyle?.borderRadius ?? 0,
    layout.image,
  );

  const selectedComponent =
    selectedLayerId && isComponentId(selectedLayerId)
      ? getComponentById(layout, selectedLayerId)
      : undefined;

  const updateComponentStyle = (changes: Partial<SlideComponentStyle>) => {
    if (!selectedComponent) {
      return;
    }
    onComponentChange(selectedComponent.id, {
      style: { ...selectedComponent.style, ...changes },
    });
  };

  const updateTableStyle = (changes: Partial<SlideTableStyle>) => {
    if (!selectedComponent) {
      return;
    }
    onComponentChange(selectedComponent.id, {
      tableStyle: { ...selectedComponent.tableStyle, ...changes },
    });
  };

  const updateChartStyle = (changes: Partial<SlideChartStyle>) => {
    if (!selectedComponent) {
      return;
    }
    onComponentChange(selectedComponent.id, {
      chartStyle: { ...selectedComponent.chartStyle, ...changes },
    });
  };

  const handleComponentImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !selectedComponent) {
      return;
    }

    try {
      const dataUrl = await readImageUpload(
        file,
        MAX_SLIDE_IMAGE_BYTES,
        'editor.uploadLabels.slideImage',
      );
      onComponentChange(selectedComponent.id, { imageUrl: dataUrl });
    } catch (error) {
      window.alert(formatImageUploadError(error, t));
    }
  };

  const updateHighlight = (index: number, value: string) => {
    const highlights = [...slide.highlights];
    highlights[index] = value;
    onSlideChange({ highlights });
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    try {
      const dataUrl = await readImageUpload(
        file,
        MAX_SLIDE_IMAGE_BYTES,
        'editor.uploadLabels.slideImage',
      );
      onSlideChange({ imageUrl: dataUrl });
    } catch (error) {
      window.alert(formatImageUploadError(error, t));
    }
  };

  if (!selectedLayerId) {
    return (
      <Panel>
        <Hint>{t('editor.inspector.emptyHint')}</Hint>
        <Field>
          {t('editor.inspector.tabLabel')}
          <TextInput
            value={slide.tabLabel}
            onChange={(event) => onSlideChange({ tabLabel: event.target.value })}
          />
        </Field>
      </Panel>
    );
  }

  if (selectedComponent) {
    const style = selectedComponent.style ?? {};
    const heading = getComponentLabel(selectedComponent, t);

    return (
      <Panel>
        <Heading>{heading}</Heading>
        <Hint>{t('editor.inspector.dragHint')}</Hint>

        {selectedComponent.type === 'text' && (
          <Field>
            {t('editor.inspector.text')}
            <TextInput
              value={selectedComponent.text ?? ''}
              onChange={(event) =>
                onComponentChange(selectedComponent.id, { text: event.target.value })
              }
            />
          </Field>
        )}

        {selectedComponent.type === 'image' && (
          <>
            <Field>
              {t('editor.inspector.imageUrl')}
              <TextInput
                value={selectedComponent.imageUrl ?? ''}
                onChange={(event) =>
                  onComponentChange(selectedComponent.id, { imageUrl: event.target.value })
                }
              />
            </Field>
            <ActionButton type="button" onClick={() => componentImageInputRef.current?.click()}>
              {t('editor.inspector.uploadImage')}
            </ActionButton>
            <HiddenInput
              ref={componentImageInputRef}
              type="file"
              accept=".svg,.png,.jpg,.jpeg,.webp,image/svg+xml,image/png,image/jpeg,image/webp"
              onChange={handleComponentImageUpload}
            />
            <Field>
              {t('editor.inspector.altText')}
              <TextInput
                value={selectedComponent.imageAlt ?? ''}
                onChange={(event) =>
                  onComponentChange(selectedComponent.id, { imageAlt: event.target.value })
                }
              />
            </Field>
            <Field as="div">
              {t('editor.inspector.cornerRadius')}
              <RangeRow>
                <RangeInput
                  type="range"
                  min={0}
                  max={maxImageCornerRadius(selectedComponent.frame)}
                  value={clampImageCornerRadius(
                    style.borderRadius ?? 0,
                    selectedComponent.frame,
                  )}
                  onChange={(event) =>
                    updateComponentStyle({
                      borderRadius: clampImageCornerRadius(
                        Number(event.target.value),
                        selectedComponent.frame,
                      ),
                    })
                  }
                />
                <RangeValue>
                  {clampImageCornerRadius(style.borderRadius ?? 0, selectedComponent.frame)}px
                </RangeValue>
              </RangeRow>
            </Field>
            <Hint>
              {t('editor.inspector.cornerRadiusMax', {
                max: maxImageCornerRadius(selectedComponent.frame),
              })}
            </Hint>
          </>
        )}

        {(selectedComponent.type === 'rectangle' ||
          selectedComponent.type === 'ellipse' ||
          selectedComponent.type === 'line') && (
          <>
            <Field as="div">
              {t('editor.inspector.fill')}
              <ColorRow>
                <ColorSwatch
                  type="color"
                  value={style.fill ?? '#ffffff'}
                  onChange={(event) => updateComponentStyle({ fill: event.target.value })}
                  aria-label={t('editor.inspector.fillColor')}
                />
                <TextInput
                  value={style.fill ?? ''}
                  onChange={(event) => updateComponentStyle({ fill: event.target.value })}
                />
              </ColorRow>
            </Field>
            {selectedComponent.type !== 'line' && (
              <>
                <Field as="div">
                  {t('editor.inspector.stroke')}
                  <ColorRow>
                    <ColorSwatch
                      type="color"
                      value={style.stroke ?? '#000000'}
                      onChange={(event) => updateComponentStyle({ stroke: event.target.value })}
                      aria-label={t('editor.inspector.strokeColor')}
                    />
                    <TextInput
                      value={style.stroke ?? ''}
                      onChange={(event) => updateComponentStyle({ stroke: event.target.value })}
                    />
                  </ColorRow>
                </Field>
                <Field as="div">
                  {t('editor.inspector.strokeWidth')}
                  <RangeRow>
                    <RangeInput
                      type="range"
                      min={0}
                      max={12}
                      value={style.strokeWidth ?? 0}
                      onChange={(event) =>
                        updateComponentStyle({ strokeWidth: Number(event.target.value) })
                      }
                    />
                    <RangeValue>{style.strokeWidth ?? 0}px</RangeValue>
                  </RangeRow>
                </Field>
              </>
            )}
            {selectedComponent.type === 'rectangle' && (
              <Field as="div">
                Corner radius
                <RangeRow>
                  <RangeInput
                    type="range"
                    min={0}
                    max={48}
                    value={style.borderRadius ?? 0}
                    onChange={(event) =>
                      updateComponentStyle({ borderRadius: Number(event.target.value) })
                    }
                  />
                  <RangeValue>{style.borderRadius ?? 0}px</RangeValue>
                </RangeRow>
              </Field>
            )}
          </>
        )}

        {selectedComponent.type === 'table' && selectedComponent.table && (() => {
          const tableData = selectedComponent.table;
          return (
          <>
            <TableEditSection>
              <Subheading>{t('editor.inspector.headers')}</Subheading>
              {tableData.headers.map((header, index) => (
                <TableEditRow key={`header-${index}`}>
                  <CompactInput
                    value={header}
                    aria-label={t('editor.inspector.headerN', { index: index + 1 })}
                    onChange={(event) =>
                      onComponentChange(selectedComponent.id, {
                        table: updateTableHeader(tableData, index, event.target.value),
                      })
                    }
                  />
                  <RemoveButton
                    type="button"
                    aria-label={t('editor.inspector.removeColumn', { index: index + 1 })}
                    disabled={tableData.headers.length <= 1}
                    onClick={() =>
                      onComponentChange(selectedComponent.id, {
                        table: removeTableColumn(tableData, index),
                      })
                    }
                  >
                    &times;
                  </RemoveButton>
                </TableEditRow>
              ))}
              <TableActions>
                <AddButton
                  type="button"
                  onClick={() =>
                    onComponentChange(selectedComponent.id, {
                      table: addTableColumn(tableData),
                    })
                  }
                >
                  {t('editor.inspector.addColumn')}
                </AddButton>
              </TableActions>
            </TableEditSection>

            <TableEditSection>
              <Subheading>{t('editor.inspector.rows')}</Subheading>
              {tableData.rows.map((row, rowIndex) => (
                <TableEditRow key={`row-${rowIndex}`}>
                  {row.map((cell, colIndex) => (
                    <CompactInput
                      key={`cell-${rowIndex}-${colIndex}`}
                      value={cell}
                      aria-label={t('editor.inspector.cell', {
                        row: rowIndex + 1,
                        col: colIndex + 1,
                      })}
                      onChange={(event) =>
                        onComponentChange(selectedComponent.id, {
                          table: updateTableCell(
                            tableData,
                            rowIndex,
                            colIndex,
                            event.target.value,
                          ),
                        })
                      }
                    />
                  ))}
                  <RemoveButton
                    type="button"
                    aria-label={t('editor.inspector.removeRow', { index: rowIndex + 1 })}
                    onClick={() =>
                      onComponentChange(selectedComponent.id, {
                        table: removeTableRow(tableData, rowIndex),
                      })
                    }
                  >
                    &times;
                  </RemoveButton>
                </TableEditRow>
              ))}
              <TableActions>
                <AddButton
                  type="button"
                  onClick={() =>
                    onComponentChange(selectedComponent.id, {
                      table: addTableRow(tableData),
                    })
                  }
                >
                  {t('editor.inspector.addRow')}
                </AddButton>
              </TableActions>
            </TableEditSection>

            <Field as="div">
              {t('editor.inspector.headerBackground')}
                  <ColorRow>
                    <ColorSwatch
                      type="color"
                      value={selectedComponent.tableStyle?.headerFill ?? appTheme.green}
                      onChange={(event) => updateTableStyle({ headerFill: event.target.value })}
                      aria-label={t('editor.inspector.headerBackground')}
                />
                <TextInput
                  value={selectedComponent.tableStyle?.headerFill ?? ''}
                  onChange={(event) => updateTableStyle({ headerFill: event.target.value })}
                />
              </ColorRow>
            </Field>
            <Field as="div">
              {t('editor.inspector.cellBackground')}
                  <ColorRow>
                    <ColorSwatch
                      type="color"
                      value={selectedComponent.tableStyle?.cellFill ?? appTheme.surface}
                      onChange={(event) => updateTableStyle({ cellFill: event.target.value })}
                      aria-label={t('editor.inspector.cellBackground')}
                />
                <TextInput
                  value={selectedComponent.tableStyle?.cellFill ?? ''}
                  onChange={(event) => updateTableStyle({ cellFill: event.target.value })}
                />
              </ColorRow>
            </Field>
            <Field as="div">
              {t('editor.inspector.borderColor')}
                  <ColorRow>
                    <ColorSwatch
                      type="color"
                      value={selectedComponent.tableStyle?.borderColor ?? appTheme.border}
                      onChange={(event) => updateTableStyle({ borderColor: event.target.value })}
                      aria-label={t('editor.inspector.borderColor')}
                />
                <TextInput
                  value={selectedComponent.tableStyle?.borderColor ?? ''}
                  onChange={(event) => updateTableStyle({ borderColor: event.target.value })}
                />
              </ColorRow>
            </Field>
          </>
          );
        })()}

        {selectedComponent.type === 'chart' && selectedComponent.chart && (() => {
          const chartData = selectedComponent.chart;
          return (
          <>
            <Field>
              {t('editor.inspector.chartTitle')}
              <TextInput
                value={chartData.title ?? ''}
                onChange={(event) =>
                  onComponentChange(selectedComponent.id, {
                    chart: updateChartTitle(chartData, event.target.value),
                  })
                }
              />
            </Field>
            <Field as="div">
              {t('editor.inspector.chartType')}
              <Select
                value={chartData.chartType}
                onChange={(event) =>
                  onComponentChange(selectedComponent.id, {
                    chart: updateChartType(chartData, event.target.value as SlideChartType),
                  })
                }
              >
                <option value="bar">{t('editor.inspector.chartTypes.bar')}</option>
                <option value="line">{t('editor.inspector.chartTypes.line')}</option>
                <option value="pie">{t('editor.inspector.chartTypes.pie')}</option>
              </Select>
            </Field>

            <TableEditSection>
              <Subheading>{t('editor.inspector.dataPoints')}</Subheading>
              {chartData.labels.map((label, index) => (
                <TableEditRow key={`chart-point-${index}`}>
                  <CompactInput
                    value={label}
                    aria-label={t('editor.inspector.labelN', { index: index + 1 })}
                    onChange={(event) =>
                      onComponentChange(selectedComponent.id, {
                        chart: updateChartLabel(chartData, index, event.target.value),
                      })
                    }
                  />
                  <CompactInput
                    type="number"
                    value={chartData.values[index] ?? 0}
                    aria-label={t('editor.inspector.valueN', { index: index + 1 })}
                    onChange={(event) =>
                      onComponentChange(selectedComponent.id, {
                        chart: updateChartValue(chartData, index, Number(event.target.value)),
                      })
                    }
                  />
                  <RemoveButton
                    type="button"
                    aria-label={t('editor.inspector.removeDataPoint', { index: index + 1 })}
                    disabled={chartData.labels.length <= 1}
                    onClick={() =>
                      onComponentChange(selectedComponent.id, {
                        chart: removeChartPoint(chartData, index),
                      })
                    }
                  >
                    &times;
                  </RemoveButton>
                </TableEditRow>
              ))}
              <TableActions>
                <AddButton
                  type="button"
                  onClick={() =>
                    onComponentChange(selectedComponent.id, {
                      chart: addChartPoint(chartData),
                    })
                  }
                >
                  {t('editor.inspector.addDataPoint')}
                </AddButton>
              </TableActions>
            </TableEditSection>

            {(chartData.chartType === 'bar' || chartData.chartType === 'line') && (
              <Field as="div">
                {chartData.chartType === 'line'
                  ? t('editor.inspector.lineColor')
                  : t('editor.inspector.primaryColor')}
                <ColorRow>
                  <ColorSwatch
                    type="color"
                    value={selectedComponent.chartStyle?.primaryColor ?? appTheme.green}
                    onChange={(event) => updateChartStyle({ primaryColor: event.target.value })}
                    aria-label={t('editor.inspector.chartColor')}
                  />
                  <TextInput
                    value={selectedComponent.chartStyle?.primaryColor ?? ''}
                    onChange={(event) => updateChartStyle({ primaryColor: event.target.value })}
                  />
                </ColorRow>
              </Field>
            )}
            <Field as="div">
              {t('editor.inspector.labelColor')}
              <ColorRow>
                <ColorSwatch
                  type="color"
                  value={selectedComponent.chartStyle?.labelColor ?? appTheme.textMuted}
                  onChange={(event) => updateChartStyle({ labelColor: event.target.value })}
                  aria-label={t('editor.inspector.labelColor')}
                />
                <TextInput
                  value={selectedComponent.chartStyle?.labelColor ?? ''}
                  onChange={(event) => updateChartStyle({ labelColor: event.target.value })}
                />
              </ColorRow>
            </Field>
            <Field as="label">
              <input
                type="checkbox"
                checked={selectedComponent.chartStyle?.showGrid ?? true}
                disabled={chartData.chartType === 'pie'}
                onChange={(event) => updateChartStyle({ showGrid: event.target.checked })}
              />
              {' '}
              {t('editor.inspector.showGrid')}
            </Field>
            <Field as="label">
              <input
                type="checkbox"
                checked={selectedComponent.chartStyle?.showValues ?? true}
                onChange={(event) => updateChartStyle({ showValues: event.target.checked })}
              />
              {' '}
              {t('editor.inspector.showValues')}
            </Field>
          </>
          );
        })()}

        {selectedComponent.type === 'text' && (
          <Field as="div">
            {t('editor.inspector.textColor')}
            <ColorRow>
              <ColorSwatch
                type="color"
                value={style.fill ?? '#1a1a1a'}
                onChange={(event) => updateComponentStyle({ fill: event.target.value })}
                aria-label={t('editor.inspector.textColor')}
              />
              <TextInput
                value={style.fill ?? ''}
                onChange={(event) => updateComponentStyle({ fill: event.target.value })}
              />
            </ColorRow>
          </Field>
        )}

        <Field as="div">
          {t('editor.inspector.opacity')}
          <RangeRow>
            <RangeInput
              type="range"
              min={0}
              max={100}
              value={Math.round((style.opacity ?? 1) * 100)}
              onChange={(event) =>
                updateComponentStyle({ opacity: Number(event.target.value) / 100 })
              }
            />
            <RangeValue>{Math.round((style.opacity ?? 1) * 100)}%</RangeValue>
          </RangeRow>
        </Field>

        <DeleteButton type="button" onClick={() => onComponentRemove(selectedComponent.id)}>
          {t('editor.inspector.removeComponent')}
        </DeleteButton>
      </Panel>
    );
  }

  const selectedElement = selectedLayerId as SlideElementId;

  return (
    <Panel>
      <Heading>{t(ELEMENT_LABEL_KEYS[selectedElement])}</Heading>

      {selectedElement === 'background' && (
        <>
          <Hint>{t('editor.inspector.backgroundHint')}</Hint>
          <Field as="div">
            {t('editor.inspector.style')}
            <Select
              value={layout.background ?? 'imageRight'}
              onChange={(event) => onBackgroundChange(event.target.value as SlideBackground)}
            >
              <option value="imageRight">{t('editor.inspector.backgrounds.imageRight')}</option>
              <option value="imageLeft">{t('editor.inspector.backgrounds.imageLeft')}</option>
              <option value="plain">{t('editor.inspector.backgrounds.plain')}</option>
            </Select>
          </Field>
        </>
      )}

      {selectedElement === 'logo' && (
        <>
          <DeckLogoPanel logoUrl={logoUrl} onLogoChange={onLogoChange} />
          <Hint>{t('editor.inspector.logoDragHint')}</Hint>
        </>
      )}

      {selectedElement === 'title' && (
        <>
          <Field>
            {t('editor.inspector.headline')}
            <TextInput
              value={slide.title}
              onChange={(event) => onSlideChange({ title: event.target.value })}
            />
          </Field>
          <Hint>{t('editor.inspector.titleDragHint')}</Hint>
        </>
      )}

      {selectedElement === 'highlights' && (
        <>
          {slide.highlights.map((highlight, index) => (
            <HighlightRow key={index}>
              <TextInput
                value={highlight}
                onChange={(event) => updateHighlight(index, event.target.value)}
              />
              <RemoveButton
                type="button"
                aria-label={t('editor.inspector.removeHighlight', { index: index + 1 })}
                onClick={() =>
                  onSlideChange({ highlights: slide.highlights.filter((_, i) => i !== index) })
                }
              >
                &times;
              </RemoveButton>
            </HighlightRow>
          ))}
          <AddButton
            type="button"
            onClick={() => onSlideChange({ highlights: [...slide.highlights, 'New highlight'] })}
          >
            {t('editor.inspector.addHighlight')}
          </AddButton>
        </>
      )}

      {selectedElement === 'image' && (
        <>
          <Field>
            Image URL
            <TextInput
              value={slide.imageUrl}
              onChange={(event) => onSlideChange({ imageUrl: event.target.value })}
            />
          </Field>
          <ActionButton type="button" onClick={() => imageInputRef.current?.click()}>
            {t('editor.inspector.uploadImage')}
          </ActionButton>
          <HiddenInput
            ref={imageInputRef}
            type="file"
            accept=".svg,.png,.jpg,.jpeg,.webp,image/svg+xml,image/png,image/jpeg,image/webp"
            onChange={handleImageUpload}
          />
          <Field>
            Alt text
            <TextInput
              value={slide.imageAlt}
              onChange={(event) => onSlideChange({ imageAlt: event.target.value })}
            />
          </Field>
          <Field as="div">
            Corner radius
            <RangeRow>
              <RangeInput
                type="range"
                min={0}
                max={builtInImageMaxRadius}
                value={builtInImageRadius}
                onChange={(event) =>
                  onImageStyleChange({
                    ...layout.imageStyle,
                    borderRadius: clampImageCornerRadius(
                      Number(event.target.value),
                      layout.image,
                    ),
                  })
                }
              />
              <RangeValue>{builtInImageRadius}px</RangeValue>
            </RangeRow>
          </Field>
          <Hint>
            {t('editor.inspector.imageDragHint', { max: builtInImageMaxRadius })}
          </Hint>
        </>
      )}
    </Panel>
  );
};

export default ElementInspector;
